import React, { useState, useEffect, useRef } from 'react';
import { projectApi } from '../../../../api/projectApi';
import './ProjectSelect.css';

const ProjectSelect = ({ 
  value, 
  onChange, 
  placeholder = "Projekt auswählen...",
  disabled = false,
  customerId = null,
  className = ''
}) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        // We fetch active projects
        const data = await projectApi.getProjects();
        setProjects(data.filter(p => p.status !== 'deleted'));
      } catch (err) {
        console.error('Failed to fetch projects', err);
      } finally {
        setLoading(false);
      }
    };
    if (!disabled) {
      fetchProjects();
    }
  }, [disabled]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredProjects = projects.filter(p => {
    if (customerId && p.customer_id !== customerId) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return p.name?.toLowerCase().includes(term) || p.project_number?.toLowerCase().includes(term);
  });

  const selectedProject = projects.find(p => p.id === value);

  return (
    <div className={`project-select-wrapper ${className}`} ref={wrapperRef}>
      <div 
        className={`project-select-header ${disabled ? 'disabled' : ''} ${isOpen ? 'open' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <div className="project-select-value">
          <svg className="project-select-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>
          {selectedProject ? (
            <span className="selected-text">
              {selectedProject.project_number} - {selectedProject.name}
            </span>
          ) : (
            <span className="placeholder-text">{placeholder}</span>
          )}
        </div>
        
        <div className="project-select-actions">
          {value && !disabled && (
            <div 
              className="project-select-clear"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null, null);
                setIsOpen(false);
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
            </div>
          )}
          <svg className="project-select-caret" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
        </div>
      </div>

      {isOpen && (
        <div className="project-select-dropdown">
          <div className="project-select-search">
            <svg className="search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
            <input 
              type="text"
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="project-select-list">
            {loading ? (
              <div className="project-select-msg">Lädt...</div>
            ) : filteredProjects.length > 0 ? (
              filteredProjects.map(p => (
                <div 
                  key={p.id}
                  className={`project-select-item ${value === p.id ? 'active' : ''}`}
                  onClick={() => {
                    onChange(p.id, p);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                >
                  <div className="project-item-number">{p.project_number}</div>
                  <div className="project-item-name">{p.name}</div>
                  {p.customer_name && (
                    <div className="project-item-customer">{p.customer_name}</div>
                  )}
                </div>
              ))
            ) : (
              <div className="project-select-msg">Keine Projekte gefunden</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectSelect;
